import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { pluginRegistry } from './plugin-loader';
import { usePlugins } from './PluginContext';
import { staticRoutes } from '@generated/plugin-registry';
import { useGlobalStore } from './store';
import { Landing } from '@/pages/Landing';
import { Login } from '@/pages/Login';

// 加载中组件
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-hermes-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-muted-foreground text-sm">加载中...</span>
      </div>
    </div>
  );
}

// 仪表盘首页
function DashboardHome() {
  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gradient-hermes mb-6">
        欢迎使用 RedMix
      </h1>
      <p className="text-muted-foreground mb-8">
        小红书矩阵运营平台 - 让运营更高效
      </p>
      
      {/* 快速统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="账号总数" value="0" change="+0%" icon="👤" />
        <StatCard title="内容数量" value="0" change="+0%" icon="📝" />
        <StatCard title="今日互动" value="0" change="+0%" icon="💬" />
        <StatCard title="粉丝增长" value="0" change="+0%" icon="📈" />
      </div>

      {/* 快速操作 */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4">快速开始</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickAction title="添加账号" icon="➕" path="/accounts/new" />
          <QuickAction title="创建内容" icon="✏️" path="/content/new" />
          <QuickAction title="查看数据" icon="📊" path="/analytics" />
          <QuickAction title="AI 助手" icon="🤖" onClick={() => {
            // TODO: 打开 AI 侧边栏
          }} />
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, change, icon }: { title: string; value: string; change: string; icon: string }) {
  const isPositive = change.startsWith('+');
  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-2xl">{icon}</span>
        <span className={`text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
          {change}
        </span>
      </div>
      <div className="text-3xl font-bold mb-1">{value}</div>
      <div className="text-sm text-muted-foreground">{title}</div>
    </div>
  );
}

function QuickAction({ title, icon, path, onClick }: { title: string; icon: string; path?: string; onClick?: () => void }) {
  const content = (
    <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors cursor-pointer">
      <span className="text-2xl">{icon}</span>
      <span className="text-sm font-medium">{title}</span>
    </div>
  );

  if (path) {
    return <a href={path}>{content}</a>;
  }
  
  return <button onClick={onClick}>{content}</button>;
}

// 404 页面
function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="text-8xl mb-4">🔍</div>
      <h1 className="text-2xl font-bold mb-2">页面未找到</h1>
      <p className="text-muted-foreground mb-6">您访问的页面不存在</p>
      <a 
        href="/"
        className="px-4 py-2 bg-hermes-500 text-white rounded-lg hover:bg-hermes-600 transition-colors"
      >
        返回首页
      </a>
    </div>
  );
}


// 受保护的路由组件
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useGlobalStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  const { isLoading } = usePlugins();
  const { isAuthenticated } = useGlobalStore();
  const pluginRoutes = pluginRegistry.getRoutes();

  if (isLoading) {
    return <LoadingFallback />;
  }

  // Combine plugin routes with static routes
  const allRoutes = [...staticRoutes, ...pluginRoutes];

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* 公开路由 */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        
        {/* 受保护的后台路由 */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardHome />
            </ProtectedRoute>
          }
        />
        
        {/* 所有插件路由 - 需要认证 */}
        {allRoutes.map((route, index) => {
          if (!route.element) return null;
          
          // 处理不同类型的 element
          // staticRoutes 中的 element 是 ComponentType (lazy component)
          // pluginRoutes 中的 element 是 ReactNode
          let routeElement: React.ReactNode;
          if (typeof route.element === 'function') {
            // lazy() 返回的组件可以直接使用 JSX
            const Component = route.element as React.ComponentType;
            routeElement = <Component />;
          } else {
            routeElement = route.element;
          }
          
          return (
            <Route 
              key={`route-${index}`} 
              path={route.path} 
              element={
                <ProtectedRoute>
                  {routeElement}
                </ProtectedRoute>
              } 
            />
          );
        })}
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

